import React, { useState, useRef, useEffect, useCallback } from "react";
import "../style/css/client/chatbot.css";

const CHAT_API_URL = "/api/agent-chat";
const AGENT_ID = "embryo";
const USER_ID = "028601";

// Simple markdown formatter for bot responses
const formatMessage = (text) => {
  if (!text) return "";
  let formatted = text
    // Remove "Sources: [...](...)" lines appended by the agent
    .replace(/\s*Sources?:\s*\[.*?\]\(.*?\)\s*/gi, "")
    // First, normalize inline bullets: insert newline before * that follows . or : or end of sentence
    .replace(/([.:])\s*\*\s+/g, "$1\n* ")
    // Bold: **text** → <strong>text</strong>
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    // Bullet points: lines starting with * or -
    .replace(/^[*\-]\s+(.+)$/gm, "<li>$1</li>")
    // Wrap consecutive <li> in <ul>
    .replace(/((?:<li>.*<\/li>\s*)+)/g, "<ul>$1</ul>")
    // Line breaks (but not inside <ul>)
    .replace(/\n/g, "<br/>");
  return formatted;
};

export const AskEmbryo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const threadId = "default_thread";
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 400);
    }
  }, [isOpen]);

  const handleOpen = () => setIsOpen(true);

  const handleClose = () => setIsOpen(false);

  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = { role: "user", content: messageText.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: messageText.trim(),
          agent_id: AGENT_ID,
          user_id: USER_ID,
          thread_id: threadId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("text/event-stream")) {
        // Handle SSE streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedText = "";

        // Add an empty bot message that we'll update progressively
        setMessages((prev) => [...prev, { role: "bot", content: "" }]);

        const processChunk = (chunk) => {
          const lines = chunk.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);
              if (data === "[DONE]") continue;
              try {
                const parsed = JSON.parse(data);
                if (parsed.content) {
                  accumulatedText += parsed.content;
                }
                if (parsed.message) {
                  accumulatedText += parsed.message;
                }
              } catch {
                // If it's not JSON, treat the raw data as text
                accumulatedText += data;
              }
            } else if (line.trim() && !line.startsWith(":")) {
              // Plain text chunk (not SSE formatted)
              accumulatedText += line;
            }
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          processChunk(chunk);

          // Update the last (bot) message progressively
          const currentText = accumulatedText;
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = { role: "bot", content: currentText };
            return updated;
          });
        }

        // If no text was captured from SSE parsing, use raw response
        if (!accumulatedText.trim()) {
          const rawText = decoder.decode();
          if (rawText.trim()) {
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: "bot", content: rawText.trim() };
              return updated;
            });
          }
        }
      } else {
        // Handle regular JSON response
        const data = await response.json();
        const botContent =
          data.response || data.message || data.content || data.answer || JSON.stringify(data);
        setMessages((prev) => [...prev, { role: "bot", content: botContent }]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        { role: "error", content: "Failed to get response. Please try again." },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, threadId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  return (
    <>
      {/* FAB Button */}
      {!isOpen && (
        <button className="chatbot-fab" onClick={handleOpen} aria-label="Open Ask Embryo chat">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
          </svg>
        </button>
      )}

      {/* Overlay */}
      {isOpen && <div className="chatbot-overlay" onClick={handleClose} />}

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">AI</div>
              <div className="chatbot-header-text">
                <h3>Ask Embryo</h3>
                <p>SLTMobitel AI Assistant</p>
              </div>
            </div>
            <button className="chatbot-close-btn" onClick={handleClose} aria-label="Close chat">
              ✕
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {/* Welcome message */}
            {messages.length === 0 && !isLoading && (
              <div className="chatbot-welcome">
                <div className="chatbot-welcome-icon">🤖</div>
                <h4>Welcome!</h4>
                <p>
                  I'm Ask Embryo, your AI assistant.
                  <br />
                  How can I help you today?
                </p>
              </div>
            )}

            {messages.map((msg, idx) => {
              if (msg.role === "error") {
                return (
                  <div key={idx} className="chatbot-error">
                    {msg.content}
                  </div>
                );
              }
              return (
                <div key={idx} className={`chatbot-message ${msg.role === "user" ? "user" : "bot"}`}>
                  <div className="chatbot-msg-avatar">
                    {msg.role === "user" ? "U" : "AI"}
                  </div>
                  {msg.role === "user" ? (
                    <div className="chatbot-bubble">{msg.content}</div>
                  ) : (
                    <div className="chatbot-bubble" dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
                  )}
                </div>
              );
            })}

            {/* Typing indicator */}
            {isLoading && messages[messages.length - 1]?.role !== "bot" && (
              <div className="chatbot-typing">
                <div className="chatbot-msg-avatar">AI</div>
                <div className="chatbot-typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form className="chatbot-input-area" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              className="chatbot-input"
              type="text"
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              autoComplete="off"
            />
            <button
              className="chatbot-send-btn"
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </form>

          {/* Powered by */}
          <div className="chatbot-powered">Powered by SLT Digital Lab</div>
        </div>
      )}
    </>
  );
};
